import { useEffect } from "react";
import seoService from "@/services/seoService";

// ======================================================
// SEO COMPONENT
// ======================================================

export default function SEO({ page = "home" }) {
  useEffect(() => {
    let mounted = true;

    const loadSEO = async () => {
      try {
        const response =
          await seoService.getPublic(page);

        if (!mounted) return;

        const seo = response?.data;

        if (!seo) return;

        applySEO(seo);
      } catch (error) {
        console.error(
          `SEO LOAD ERROR [${page}]:`,
          error
        );
      }
    };

    loadSEO();

    return () => {
      mounted = false;
    };
  }, [page]);

  return null;
}

// ======================================================
// APPLY SEO
// ======================================================

function applySEO(seo) {
  // ====================================================
  // TITLE
  // ====================================================

  if (seo.metaTitle) {
    document.title = seo.metaTitle;
  }

  // ====================================================
  // META DESCRIPTION
  // ====================================================

  setMeta(
    "name",
    "description",
    seo.metaDescription || ""
  );

  // ====================================================
  // META KEYWORDS
  // ====================================================

  const keywords =
    seo.metaKeywords?.length
      ? seo.metaKeywords.join(", ")
      : seo.focusKeywords?.length
        ? seo.focusKeywords.join(", ")
        : "";

  if (keywords) {
    setMeta(
      "name",
      "keywords",
      keywords
    );
  }

  // ====================================================
  // ROBOTS
  // ====================================================

  if (seo.robots) {
    setMeta(
      "name",
      "robots",
      seo.robots
    );
  }

  // ====================================================
  // CANONICAL
  // ====================================================

  if (seo.canonicalUrl) {
    setCanonical(
      seo.canonicalUrl
    );
  }

  // ====================================================
  // OPEN GRAPH
  // ====================================================

  if (seo.ogTitle) {
    setMeta(
      "property",
      "og:title",
      seo.ogTitle
    );
  }

  if (seo.ogDescription) {
    setMeta(
      "property",
      "og:description",
      seo.ogDescription
    );
  }

  if (seo.ogImage) {
    setMeta(
      "property",
      "og:image",
      seo.ogImage
    );
  }

  setMeta(
    "property",
    "og:type",
    "website"
  );

  if (seo.canonicalUrl) {
    setMeta(
      "property",
      "og:url",
      seo.canonicalUrl
    );
  }

  // ====================================================
  // TWITTER
  // ====================================================

  if (seo.twitterCard) {
    setMeta(
      "name",
      "twitter:card",
      seo.twitterCard
    );
  }

  if (seo.twitterTitle) {
    setMeta(
      "name",
      "twitter:title",
      seo.twitterTitle
    );
  }

  if (seo.twitterDescription) {
    setMeta(
      "name",
      "twitter:description",
      seo.twitterDescription
    );
  }

  if (seo.twitterImage) {
    setMeta(
      "name",
      "twitter:image",
      seo.twitterImage
    );
  }

  // ====================================================
  // JSON-LD SCHEMA
  // ====================================================

  removeSchemaScripts();

  if (seo.schema) {
    addSchemaScript(seo.schema);
  }

  if (seo.faqSchema) {
    addSchemaScript(seo.faqSchema);
  }
}

// ======================================================
// META HELPER
// ======================================================

function setMeta(attribute, key, content) {
  if (!content) return;

  let element = document.head.querySelector(
    `meta[${attribute}="${key}"]`
  );

  if (!element) {
    element =
      document.createElement("meta");

    element.setAttribute(
      attribute,
      key
    );

    document.head.appendChild(element);
  }

  element.setAttribute(
    "content",
    content
  );
}

// ======================================================
// CANONICAL
// ======================================================

function setCanonical(url) {
  let link = document.head.querySelector(
    'link[rel="canonical"]'
  );

  if (!link) {
    link =
      document.createElement("link");

    link.setAttribute(
      "rel",
      "canonical"
    );

    document.head.appendChild(link);
  }

  link.setAttribute(
    "href",
    url
  );
}

// ======================================================
// ADD JSON-LD
// ======================================================

function addSchemaScript(schema) {
  const script =
    document.createElement("script");

  script.type =
    "application/ld+json";

  script.setAttribute(
    "data-cet-seo-schema",
    "true"
  );

  script.textContent =
    JSON.stringify(schema);

  document.head.appendChild(script);
}

// ======================================================
// REMOVE OLD JSON-LD
// ======================================================

function removeSchemaScripts() {
  document
    .head
    .querySelectorAll(
      'script[data-cet-seo-schema="true"]'
    )
    .forEach((script) => {
      script.remove();
    });
}